import { useCallback, useEffect, useRef, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import { HiChevronDown } from "react-icons/hi";
import { useLocation } from "react-router-dom";

import { newNavExcludesNavigation } from "../../Utilities/Navigation/utils";
import { useUser } from "../../Hooks/useUser";
import { useNavClickTracker } from "MVVM/Hooks/useNavClickTracker";
import { useSteambot } from "MVVM/Hooks/useSteambot";
import { useIsDesktopApp } from "../../Hooks/useIsDesktopApp";
import { useIsMobileApp } from "../../Hooks/useIsMobileApp";
import {
  NAV_BAR,
  NAV_CATEGORIES,
  TNavDestination,
  findActiveCategoryKey,
  resolveCta,
} from "./links";
import { MegaMenu } from "./MegaMenu";
import { MobileNav } from "./MobileNav";
import { SuperNav } from "./SuperNav";
import { useSteambotSignupModal } from "./SteambotSignupModal";
import {
  CategoryLink,
  CategoryRow,
  CategoryTrigger,
  CategoryTriggerAnchor,
  CategoryTriggerLink,
  HomeLink,
  NavBar,
  NavInner,
  StyledHiHome,
} from "./styledComponents";

const HOVER_INTENT_MS = 150;
/**
 * Switching between open panels waits too, rather than swapping on contact.
 * Travelling from a trigger down into the panel often clips a neighbouring
 * trigger on the way, and an instant swap meant the panel changed underneath
 * the cursor before it arrived. Leaving the trigger cancels the pending open,
 * so a pass-through costs nothing while a deliberate hover still lands.
 */
const SWITCH_INTENT_MS = 180;
const CLOSE_INTENT_MS = 320;

// The modal has no URL of its own, so that click reports no destination.
const destinationUrl = (destination: TNavDestination | null) => {
  if (destination?.kind === "to") return destination.to;
  if (destination?.kind === "href") return destination.href;
  return undefined;
};

const SiteNav = ({ standalone }: { standalone?: boolean }) => {
  const { pathname } = useLocation();
  const isDesktopView = useIsDesktopApp();
  const isMobileApp = useIsMobileApp();
  /**
   * useUser, not the IsUserLoggedIn atom: that atom computes its default once
   * at module load from localStorage and nothing ever sets it, so it does not
   * flip until a full page reload. Reading it here left the bar in its
   * signed-out shape until you refreshed.
   */
  const { allstarUser } = useUser();
  const isLoggedIn = Boolean(allstarUser?.loggedIn);
  const trackNavClick = useNavClickTracker("site_nav");
  // Shared with the panel below rather than queried twice. The bubble already
  // holds this query app-wide, and Apollo dedupes the concurrent copy.
  const { enrolled: steambotEnrolled } = useSteambot();

  const [openKey, setOpenKey] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const triggerRefs = useRef<Record<string, HTMLElement | null>>({});
  const openTimer = useRef<ReturnType<typeof setTimeout>>();
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();
  const steambotModal = useSteambotSignupModal();

  const barObserver = useRef<ResizeObserver>();
  const barRef = useCallback((node: HTMLElement | null) => {
    barObserver.current?.disconnect();

    if (!node) {
      document.documentElement.style.removeProperty("--site-header-height");
      document.documentElement.style.removeProperty("--site-gutter");
      return;
    }

    /**
     * Publishes the gutter as well as the height, so a page that renders its own
     * chrome under this bar can line up with it without repeating the number or
     * being told the nav is there. Read from the strip rather than hardcoded, so
     * it stays right across the breakpoint. Unset when the nav is absent, and
     * every consumer supplies its own fallback for that case.
     */
    const publish = () => {
      document.documentElement.style.setProperty(
        "--site-header-height",
        `${node.offsetHeight}px`,
      );

      const inner = node.querySelector("[data-nav-gutter]");
      if (inner)
        document.documentElement.style.setProperty(
          "--site-gutter",
          getComputedStyle(inner).paddingLeft,
        );
    };

    publish();
    barObserver.current = new ResizeObserver(publish);
    barObserver.current.observe(node);
  }, []);

  // Standalone pages mount the nav themselves, and their own routes sit in
  // excludedPaths, so the path guard would wrongly blank them.
  const hidden =
    (!standalone && newNavExcludesNavigation(pathname)) ||
    isDesktopView.isDesktop ||
    isMobileApp.isMobileApp;

  // Warms the panel art on mount so the first panel does not open onto an
  // empty rail. Effects run after paint, so this stays off the critical path.
  useEffect(() => {
    if (hidden) return;
    NAV_CATEGORIES.forEach(({ art, glass }) =>
      [art, glass].forEach((src) => {
        if (src) new Image().src = src;
      }),
    );
  }, [hidden]);

  const clearTimers = useCallback(() => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  // Navigating with a panel open would otherwise leave it hanging over the new
  // page.
  useEffect(() => {
    setOpenKey(null);
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!openKey) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpenKey(null);
      triggerRefs.current[openKey]?.focus();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [openKey]);

  const handleEnter = (key: string) => {
    clearTimers();
    openTimer.current = setTimeout(
      () => setOpenKey(key),
      openKey ? SWITCH_INTENT_MS : HOVER_INTENT_MS,
    );
  };

  const handleLeave = () => {
    clearTimers();
    closeTimer.current = setTimeout(() => setOpenKey(null), CLOSE_INTENT_MS);
  };

  // One bar and one category set for both auth states. The strip above is
  // permanent too, so signing in swaps its contents rather than moving them.
  const categories = NAV_CATEGORIES;
  const barEntries = NAV_BAR;
  const activeKey = findActiveCategoryKey(pathname, categories);
  const openCategory = categories.find((category) => category.key === openKey);
  const handleTriggerKeyDown = (
    event: ReactKeyboardEvent<HTMLElement>,
    index: number,
  ) => {
    // Enter navigates now, so the panel needs its own key to open on.
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      clearTimers();
      setOpenKey(categories[index].key);
      return;
    }
    if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
    event.preventDefault();
    const step = event.key === "ArrowRight" ? 1 : -1;
    const next = (index + step + categories.length) % categories.length;
    triggerRefs.current[categories[next].key]?.focus();
  };

  if (hidden) return <></>;

  /**
   * Hover still opens the panel; the click is the category's own destination.
   * Shared by both cohorts so the trigger's behaviour cannot drift between them.
   */
  const renderTrigger = (
    category: (typeof categories)[number],
    index: number,
  ) => {
    const { destination } = resolveCta(category, {
      isLoggedIn,
      steambotEnrolled,
    });
    const shared = {
      key: category.key,
      ref: (node: HTMLElement | null) => {
        triggerRefs.current[category.key] = node;
      },
      $open: openKey === category.key,
      "aria-current": (activeKey === category.key ? "page" : undefined) as
        | "page"
        | undefined,
      "aria-expanded": openKey === category.key,
      "aria-controls": `nav-panel-${category.key}`,
      onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) =>
        handleTriggerKeyDown(event, index),
      onMouseEnter: () => handleEnter(category.key),
      onMouseLeave: handleLeave,
      children: (
        <>
          {category.label}
          <HiChevronDown aria-hidden="true" />
        </>
      ),
    };

    const onClick = () => {
      trackNavClick(category.label, destinationUrl(destination));
      clearTimers();
      setOpenKey(null);
      if (destination?.kind === "modal") steambotModal.open();
    };

    if (destination?.kind === "to")
      return (
        <CategoryTriggerLink
          {...shared}
          to={destination.to}
          onClick={onClick}
        />
      );

    if (destination?.kind === "href")
      return (
        <CategoryTriggerAnchor
          {...shared}
          href={destination.href}
          target="_blank"
          rel="noreferrer"
          onClick={onClick}
        />
      );

    return <CategoryTrigger {...shared} onClick={onClick} />;
  };

  return (
    <NavBar ref={barRef} $standalone={standalone}>
      <SuperNav
        isLoggedIn={isLoggedIn}
        pathname={pathname}
        standalone={standalone}
        onOpenMobile={() => setMobileOpen(true)}
      />
      <NavInner $standalone={standalone}>
        <CategoryRow aria-label="Primary">
          <HomeLink
            to="/"
            aria-label="Home"
            onClick={() => trackNavClick("Home", "/")}
          >
            <StyledHiHome aria-hidden="true" />
          </HomeLink>
          {barEntries.map((entry, index) => {
            if (entry.kind === "link")
              return (
                <CategoryLink
                  key={entry.to}
                  to={entry.to}
                  onClick={() => trackNavClick(entry.label, entry.to)}
                >
                  {entry.label}
                </CategoryLink>
              );

            const category = categories.find((c) => c.key === entry.key);
            return category ? renderTrigger(category, index) : null;
          })}
        </CategoryRow>
      </NavInner>
      {openCategory && (
        <MegaMenu
          category={openCategory}
          isLoggedIn={isLoggedIn}
          panelId={`nav-panel-${openCategory.key}`}
          pathname={pathname}
          standalone={standalone}
          onNavigate={() => setOpenKey(null)}
          onOpenSteambot={steambotModal.open}
          steambotEnrolled={steambotEnrolled}
          onMouseEnter={clearTimers}
          onMouseLeave={handleLeave}
        />
      )}
      <MobileNav
        open={mobileOpen}
        isLoggedIn={isLoggedIn}
        onClose={() => setMobileOpen(false)}
      />
      {steambotModal.portalOpen && steambotModal.Portal}
    </NavBar>
  );
};

export { SiteNav };
